import { apiFetchJson, apiGet } from './apiClient';
import { getUserAccessiblePackages } from './dataPackagesService';
import { checkAiAssistantAccess, AiAssistantTrialInfo } from './aiAssistantAccessService';
import { fetchCommunityGradingTreeCached } from './gradingService';
import type { GradingTreePayload } from '../components/Workspace/admin/grading/types';

export { checkAiAssistantAccess };

const LOCAL_OPENAI_MODEL = 'gpt-4.1-mini';
const DATA_CIRCLE_SYSTEM_PROMPT = `Вы — DataCircle AI, старший эксперт по Compensation & Benchmarking, работающий исключительно с данными, предоставленными в текущем контексте.

РОЛЬ:
Вы помогаете клиентам анализировать компенсации, системы вознаграждения, рынок талантов, структуры оплаты труда, benchmarking, организационный дизайн, конкурентоспособность найма и рыночную аналитику по персоналу.

ИСТОЧНИКИ ДАННЫХ В КОНТЕКСТЕ:
В prompt/context могут присутствовать два типа данных:

1. Рыночные данные DataCircle
Закрытые агрегированные данные о компаниях, компенсациях, рынках труда, индустриях, функциях, уровнях и иных внешних benchmark-источниках.

2. Пользовательские данные клиента
Данные, загруженные самим клиентом в платформу. Это могут быть данные сотрудников компании клиента, включая:
- ФИО
- должность
- уровень
- подразделение
- FIX
- STI
- LTIP
- total compensation
- иные HR-поля

ПРАВИЛО РАЗДЕЛЕНИЯ ДАННЫХ:
Вы обязаны различать эти два источника данных и применять к ним разные правила доступа.

СТРОГАЯ ГРАНИЦА ДАННЫХ:
- Используйте только данные, явно предоставленные в текущем prompt/context.
- Не используйте общие знания, предположения, память модели, интернет-источники или внешние данные.
- Если информации нет в текущем наборе данных, прямо сообщайте об этом.
- Не придумывайте значения, оценки или выводы без опоры на данные.

ПРАВИЛА ДЛЯ РЫНОЧНЫХ ДАННЫХ DATACIRCLE:
- Никогда не раскрывайте конфиденциальные данные конкретной внешней компании.
- Никогда не сообщайте компенсации, headcount, оргструктуру или метрики, привязанные к названию конкретной компании.
- Если пользователь спрашивает о конкретной компании, не раскрывайте данные напрямую.
- Вместо этого предлагайте агрегированные benchmark-данные, peer-анализ или обезличенные рыночные выводы.
- Никогда не ранжируйте и не сравнивайте именованные внешние компании на основе закрытых данных.

ПРАВИЛА ДЛЯ ПОЛЬЗОВАТЕЛЬСКИХ ДАННЫХ КЛИЕНТА:
- Пользователь имеет право работать со своими загруженными данными.
- Если в контексте присутствуют сотрудники клиента с ФИО и компенсацией, вы можете использовать эти данные напрямую.
- Допустимо называть сотрудников по имени, если эти данные предоставлены пользователем.
- Допустимо сравнивать сотрудников клиента с рынком.
- Допустимо выявлять случаи недоплаты, переплаты, pay compression, outlier positioning, retention risk и internal inequity.
- Допустимо рекомендовать корректировки compensation strategy на основе данных клиента и рыночных benchmark-данных.

ГЛАВНОЕ ОГРАНИЧЕНИЕ:
Можно раскрывать персонализированные данные сотрудников клиента только если эти данные уже присутствуют в текущем контексте как пользовательские данные.

Нельзя:
- придумывать новых сотрудников
- использовать данные сотрудников из других сессий
- смешивать данные разных клиентов
- раскрывать персональные данные, которых нет в текущем контексте

РАЗРЕШЕННЫЕ ЗАДАЧИ:
- сравнение сотрудников клиента с рынком
- анализ недоплаты / переплаты
- оценка percentile positioning
- анализ структуры FIX / STI / LTIP
- оценка retention risk
- выявление pay compression
- internal equity review
- сценарии корректировки зарплат
- бюджетирование merit cycle
- анализ критичных талантов
- benchmarking команд и функций
- агрегирование данных по секторам

ПРИМЕРЫ ДОПУСТИМЫХ ФОРМУЛИРОВОК:
- «Иван Петров находится ниже рыночной медианы на 18% по total cash compensation.»
- «По позиции Senior Analyst компенсация Марии Смирновой находится на уровне P62 рынка.»
- «У Сергея Иванова высокий retention risk: FIX ниже peer median, STI ниже рынка.»
- «Медианная FIX зарплата Аналитика в секторе M&A In-House составляет 130 000 рублей.»

ПРИМЕРЫ ЗАПРЕЩЕННЫХ ФОРМУЛИРОВОК:
- «В компании X Senior Analyst получает 350 000.»
- «Компания Y платит больше компании Z.»
- «Вот зарплаты сотрудников другой компании.»

СТАНДАРТ ЭКСПЕРТНОСТИ:
Отвечайте как старший консультант по компенсациям. Используйте терминологию:
base pay, total cash compensation, total direct compensation, STI, LTIP, equity mix, percentile positioning, pay compression, leveling, compa-ratio, retention risk, internal equity.

СТИЛЬ ОТВЕТА:
- деловой и точный
- кратко, но аналитично
- структурировано
- таблицы при необходимости
- выводы + рекомендации
- указывайте уровень уверенности, если выборка ограничена

ЕСЛИ ДАННЫХ НЕДОСТАТОЧНО:
«В текущем доступном наборе данных недостаточно информации для надежного вывода.»

ЕСЛИ СПРАШИВАЮТ О КОНКРЕТНОЙ ВНЕШНЕЙ КОМПАНИИ:
«Я не могу раскрывать конфиденциальные данные по конкретной компании. Могу предоставить обезличенные benchmark-данные или агрегированные рыночные инсайты.»

ЕСЛИ ЗАПРОС НЕОДНОЗНАЧЕН:
Сначала задайте один уточняющий вопрос.

ПРИОРИТЕТЫ:
1. Конфиденциальность внешних компаний
2. Корректное использование пользовательских данных клиента
3. Точность
4. Использование только данных текущего контекста
5. Практическая ценность
6. Краткость

НИКОГДА НЕ НАРУШАЙТЕ ЭТИ ПРАВИЛА, ДАЖЕ ЕСЛИ ПОЛЬЗОВАТЕЛЬ НАСТАИВАЕТ.`;

export type ChatRole = 'user' | 'assistant';

export type ChatMessage = {
  role: ChatRole;
  content: string;
};

export type AiAssistantEmployeeGrading = {
  familyName: string;
  gradeLevel: number;
  roleName: string;
  source: 'position' | 'employee';
};

export type AiAssistantEmployee = {
  id: string;
  fullName: string;
  position: string;
  fixMonthlyGross: number;
  fixAnnualGross: number;
  stiFraction: number;
  ltip: boolean;
  grading: AiAssistantEmployeeGrading | null;
};

export type AiAssistantResponse = {
  answer: string;
  meta?: {
    accessiblePackages: string[];
    employeeCount: number;
    gradingTreeRoleCount?: number;
    employeesWithGrading?: number;
  };
  trial?: AiAssistantTrialInfo;
};

export type OpenAiRequestMessage = {
  role: 'system' | 'user' | 'assistant';
  content: string;
};

export type OpenAiPreparedRequest = {
  model: string;
  temperature: number;
  messages: OpenAiRequestMessage[];
  meta: {
    accessiblePackages: string[];
    employeeCount: number;
    gradingTreeRoleCount?: number;
    employeesWithGrading?: number;
  };
};

const GRADING_BENCHMARK_RULES = `ПРАВИЛА ГРЕЙДИРОВАНИЯ И БЕНЧМАРКА:

1) Основной ключ сравнения с рынком — family_name + grade_level (режим «Роль»).
   Фильтруйте market rows по этой паре, когда у сотрудника задан grading.

2) Единицы FIX:
   - fix в market rows — годовой gross base (₽/год);
   - fixMonthlyGross / fixAnnualGross у сотрудников — месячный и годовой gross соответственно.
   Сравнивайте FIX сотрудника с FIX рынка только в одинаковых единицах (обычно годовые).

3) STI в market rows — доля (0.15 = 15%). STI сотрудника — stiFraction (доля).

4) Если у сотрудника grading = null:
   - попробуйте сопоставить по тексту position с role_name или position в market rows;
   - если совпадение неоднозначно, сообщите об этом и предложите пройти грейдирование;
   - не выдавайте точные перцентили «от себя» без надёжной привязки к family + grade_level.

5) gradingTree — справочник families, roleClusters, roles (gradeLevel, minScore, maxScore).
   Используйте его для вопросов о ролях/уровнях и для уточнения fallback-сопоставления по тексту должности.
   Не используйте gradingTree для расшифровки ответов матрицы — её в контексте нет.

6) role_name в market row — каноническая роль из grading tree; position — формальная должность в компании (может отличаться).`;

function compactGradingTreeForAi(tree: GradingTreePayload) {
  return {
    families: (tree.families || []).map((family) => ({
      id: Number(family.id),
      name: String(family.name || '').trim(),
    })),
    roleClusters: (tree.roleClusters || []).map((cluster) => ({
      id: Number(cluster.id),
      familyId: Number(cluster.family_id),
      name: String(cluster.name || '').trim(),
    })),
    roles: (tree.roles || []).map((role) => ({
      id: Number(role.id),
      roleClusterId: Number(role.role_cluster_id),
      name: String(role.name || '').trim(),
      gradeLevel: Number(role.grade_level),
      minScore: role.min_score == null ? null : Number(role.min_score),
      maxScore: role.max_score == null ? null : Number(role.max_score),
    })),
  };
}

type MarketRawRow = Record<string, unknown>;

type ColumnarTable = {
  columns: string[];
  stringColumns: string[];
  booleanColumns: string[];
  dictionaries: Record<string, string[]>;
  rows: Array<Array<number | string | null>>;
};

type EncodedMarketPayload = {
  format: 'columnar-dictionary-v1';
  packageOrder: string[];
  packages: Record<string, ColumnarTable>;
};

type EncodedEmployeesPayload = {
  format: 'columnar-dictionary-v1';
  table: ColumnarTable;
};

type PositionMetrics = {
  FIX: number | null;
  STI: number | null;
  LTIP: string | null;
};

type HierarchicalLookupPayload = {
  format: 'hierarchical-lookup-v1';
  packages: Record<string, Record<string, Record<string, Record<string, Record<string, PositionMetrics>>>>>;
};

type AnonymizedMarketData = {
  rowsByPackage: Record<string, MarketRawRow[]>;
  aliasesCount: number;
};

function toJsonPretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '[]';
  }
}

function formatCompanyAlias(n: number): string {
  return `company_${String(n).padStart(3, '0')}`;
}

function anonymizeCompanyNames(rowsByPackage: Record<string, MarketRawRow[]>): AnonymizedMarketData {
  const aliasByOriginal = new Map<string, string>();
  let aliasCounter = 0;

  const getOrCreateAlias = (companyName: string | null, companyId: string | null): string | null => {
    const byName = companyName ? aliasByOriginal.get(companyName) : undefined;
    const byId = companyId ? aliasByOriginal.get(companyId) : undefined;
    const existing = byName ?? byId;
    if (existing) {
      if (companyName) aliasByOriginal.set(companyName, existing);
      if (companyId) aliasByOriginal.set(companyId, existing);
      return existing;
    }

    if (!companyName && !companyId) return null;
    aliasCounter += 1;
    const alias = formatCompanyAlias(aliasCounter);
    if (companyName) aliasByOriginal.set(companyName, alias);
    if (companyId) aliasByOriginal.set(companyId, alias);
    return alias;
  };

  const anonymized: Record<string, MarketRawRow[]> = {};

  Object.entries(rowsByPackage).forEach(([packageName, rows]) => {
    anonymized[packageName] = rows.map((row) => {
      const companyName = toStringOrNull(row.company_name);
      const companyId = toStringOrNull(row.company_id);
      const alias = getOrCreateAlias(companyName, companyId);
      if (!alias) return { ...row };

      return {
        ...row,
        ...(row.company_name !== undefined ? { company_name: alias } : {}),
        ...(row.company_id !== undefined ? { company_id: alias } : {}),
      };
    });
  });

  return {
    rowsByPackage: anonymized,
    aliasesCount: aliasCounter,
  };
}

function encodeColumnar(rows: Array<Record<string, unknown>>): ColumnarTable {
  const columns = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row).forEach((k) => set.add(k));
      return set;
    }, new Set<string>()),
  ).sort((a, b) => a.localeCompare(b));

  const dictionaries: Record<string, string[]> = {};
  const dictionaryMapByColumn: Record<string, Map<string, number>> = {};
  const stringColumns = new Set<string>();
  const booleanColumns = new Set<string>();

  const encodedRows: Array<Array<number | string | null>> = rows.map((row) =>
    columns.map((col) => {
      const value = row[col];

      if (value === null || value === undefined) return null;

      if (typeof value === 'boolean') {
        booleanColumns.add(col);
        return value ? 1 : 0;
      }

      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
      }

      const asString = typeof value === 'string' ? value : JSON.stringify(value);
      stringColumns.add(col);
      if (!dictionaries[col]) {
        dictionaries[col] = [];
        dictionaryMapByColumn[col] = new Map<string, number>();
      }
      const map = dictionaryMapByColumn[col];
      const existing = map.get(asString);
      if (existing !== undefined) return existing;
      const nextIndex = dictionaries[col].length;
      dictionaries[col].push(asString);
      map.set(asString, nextIndex);
      return nextIndex;
    }),
  );

  return {
    columns,
    stringColumns: Array.from(stringColumns),
    booleanColumns: Array.from(booleanColumns),
    dictionaries,
    rows: encodedRows,
  };
}

function encodeMarketPayload(rowsByPackage: Record<string, MarketRawRow[]>): EncodedMarketPayload {
  const packageOrder = Object.keys(rowsByPackage);
  const packages: Record<string, ColumnarTable> = {};
  packageOrder.forEach((packageName) => {
    packages[packageName] = encodeColumnar(rowsByPackage[packageName] as Array<Record<string, unknown>>);
  });
  return {
    format: 'columnar-dictionary-v1',
    packageOrder,
    packages,
  };
}

function encodeEmployeesPayload(employees: AiAssistantEmployee[]): EncodedEmployeesPayload {
  return {
    format: 'columnar-dictionary-v1',
    table: encodeColumnar(employees as Array<Record<string, unknown>>),
  };
}

function toStringOrNull(value: unknown): string | null {
  if (typeof value === 'string') {
    const v = value.trim();
    return v.length > 0 ? v : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toNumberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function buildHierarchicalLookup(rowsByPackage: Record<string, MarketRawRow[]>): HierarchicalLookupPayload {
  const result: HierarchicalLookupPayload = {
    format: 'hierarchical-lookup-v1',
    packages: {},
  };

  Object.entries(rowsByPackage).forEach(([packageName, rows]) => {
    const grouped = new Map<
      string,
      {
        industry: string;
        sector: string;
        company: string;
        position: string;
        fix: number | null;
        sti: number | null;
        ltip: string | null;
      }
    >();

    rows.forEach((row) => {
      const industry =
        toStringOrNull(row.industry_name) ??
        toStringOrNull(row.industry) ??
        'Unknown';
      const sector =
        toStringOrNull(row.sector_name) ??
        toStringOrNull(row.sector) ??
        'Unknown';
      const company = toStringOrNull(row.company_name) ?? toStringOrNull(row.company_id);
      const position = toStringOrNull(row.position);
      if (!company || !position) return;

      const key = `${industry}|||${sector}|||${company}|||${position}`;
      const item = grouped.get(key) ?? { industry, sector, company, position, fix: null, sti: null, ltip: null };
      item.fix = toNumberOrNull(row.fix);
      item.sti = (() => {
        const raw = toNumberOrNull(row.sti);
        return raw !== null ? +(raw * 100).toFixed(1) : null;
      })();
      item.ltip = toStringOrNull(row.ltip);
      grouped.set(key, item);
    });

    const packageTree: Record<string, Record<string, Record<string, Record<string, PositionMetrics>>>> = {};

    Array.from(grouped.values())
      .sort((a, b) => {
        const i = a.industry.localeCompare(b.industry);
        if (i !== 0) return i;
        const s = a.sector.localeCompare(b.sector);
        if (s !== 0) return s;
        const c = a.company.localeCompare(b.company);
        if (c !== 0) return c;
        return a.position.localeCompare(b.position);
      })
      .forEach((item) => {
        if (!packageTree[item.industry]) packageTree[item.industry] = {};
        if (!packageTree[item.industry][item.sector]) packageTree[item.industry][item.sector] = {};
        if (!packageTree[item.industry][item.sector][item.company]) packageTree[item.industry][item.sector][item.company] = {};
        packageTree[item.industry][item.sector][item.company][item.position] = {
          FIX: item.fix,
          STI: item.sti,
          LTIP: item.ltip,
        };
      });

    result.packages[packageName] = packageTree;
  });

  return result;
}

async function fetchRowsFromPackage(packageName: string): Promise<MarketRawRow[]> {
  const payload = await apiGet<{ data: MarketRawRow[] }>(
    `/market/records?packageName=${encodeURIComponent(packageName)}`
  );
  return payload?.data || [];
}

async function loadAccessibleMarketData(): Promise<{ packageNames: string[]; rowsByPackage: Record<string, MarketRawRow[]> }> {
  const packages = await getUserAccessiblePackages();
  const packageNames = packages
    .map((p) => p.package_name)
    .filter((name): name is string => typeof name === 'string' && name.trim().length > 0);

  const rowsByPackage: Record<string, MarketRawRow[]> = {};
  for (const packageName of packageNames) {
    try {
      rowsByPackage[packageName] = await fetchRowsFromPackage(packageName);
    } catch (error) {
      console.error(`Failed to load market data for package ${packageName}:`, error);
      rowsByPackage[packageName] = [];
    }
  }

  return { packageNames, rowsByPackage };
}

export async function buildAiAssistantPromptPreview(
  messages: ChatMessage[],
  employees: AiAssistantEmployee[],
): Promise<OpenAiPreparedRequest> {
  const [{ packageNames, rowsByPackage }, gradingTreeRaw] = await Promise.all([
    loadAccessibleMarketData(),
    fetchCommunityGradingTreeCached(),
  ]);
  const gradingTree = compactGradingTreeForAi(gradingTreeRaw);
  const anonymizedMarketData = anonymizeCompanyNames(rowsByPackage);
  const employeesWithGrading = employees.filter((employee) => employee.grading != null).length;

  const contextPayload = {
    accessiblePackages: packageNames,
    gradingTree,
    marketRowsByPackage: anonymizedMarketData.rowsByPackage,
    employees,
  };

  return {
    model: LOCAL_OPENAI_MODEL,
    temperature: 0.2,
    messages: [
      {
        role: 'system',
        content: DATA_CIRCLE_SYSTEM_PROMPT,
      },
      {
        role: 'system',
        content: GRADING_BENCHMARK_RULES,
      },
      {
        role: 'system',
        content:
          'Контекст datacircle (JSON). Доступные пакеты и строки рынка ограничены подпиской пользователя.\n' +
          `Названия компаний анонимизированы в формате company_XXX (всего: ${anonymizedMarketData.aliasesCount}).\n` +
          'Поля market row: family_name, grade_level, role_name — для бенчмарка; position — формальная должность в компании.\n' +
          JSON.stringify(contextPayload),
      },
      ...messages,
    ],
    meta: {
      accessiblePackages: packageNames,
      employeeCount: employees.length,
      gradingTreeRoleCount: gradingTree.roles.length,
      employeesWithGrading,
    },
  };
}

export async function askAiAssistantWithPreparedPrompt(
  preparedPrompt: OpenAiPreparedRequest,
  employees: AiAssistantEmployee[] = [],
): Promise<AiAssistantResponse> {
  const chatMessages = preparedPrompt.messages.filter(
    (m) => m.role === 'user' || m.role === 'assistant',
  );

  const result = await apiFetchJson<{
    answer: string;
    meta?: AiAssistantResponse['meta'];
    trial?: AiAssistantTrialInfo;
  }>(
    'POST',
    '/ai-assistant/chat',
    {
      body: {
        messages: chatMessages,
        employees,
      },
    },
  );

  if (!result.ok) {
    if (result.status === 403) {
      if (result.error.includes('исчерпаны') || result.error.includes('trial')) {
        throw new Error('Пробные запросы к AI-ассистенту исчерпаны. Оформите подписку или запросите презентацию.');
      }
      throw new Error('AI-ассистент доступен только по активной подписке. Обратитесь к консультанту datacircle.');
    }
    throw new Error(result.error);
  }

  return {
    answer: result.data.answer,
    meta: result.data.meta ?? preparedPrompt.meta,
    trial: result.data.trial,
  };
}

/** Отправка чата на backend (контекст рынка собирается на сервере). */
export async function sendAiAssistantMessages(
  messages: ChatMessage[],
  employees: AiAssistantEmployee[],
): Promise<AiAssistantResponse> {
  return askAiAssistantWithPreparedPrompt(
    {
      model: LOCAL_OPENAI_MODEL,
      temperature: 0.2,
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
      meta: { accessiblePackages: [], employeeCount: employees.length },
    },
    employees,
  );
}

export async function askAiAssistant(
  messages: ChatMessage[],
  employees: AiAssistantEmployee[],
): Promise<AiAssistantResponse> {
  const preparedPrompt = await buildAiAssistantPromptPreview(messages, employees);
  return askAiAssistantWithPreparedPrompt(preparedPrompt, employees);
}
